import {equals, sortBy} from 'ramda';

import type {ColourResponseBody, OfferedTrimRequestBody} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';
import {PricedItemGroupEntryChanged} from '../types/PricedItemGroupEntryChanged';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

const storedRows = (rows: ColourResponseBody['trims']) =>
  sortBy(
    ({trimId}) => trimId,
    rows.map(({trimId, priceNet, costNet}) => ({trimId, priceNet, costNet: costNet ?? null}))
  );

/**
 * The entry of the trims of a stored colour for an open group (Q2): the colour edit dialog sends its
 * other fields at once, as they are no price list change, and only the trims join the group. The
 * build reads the colour again and sends its current version, so the scalar save and other writes of
 * the colour do not make the batch stale. It fails with PricedItemGroupEntryChanged when the stored
 * rows changed after the entry was added, as the trims were chosen from them.
 */
export const colourTrimsEntry = (options: {
  /** The colour as the dialog read it: the rows the trims were chosen from. */
  colour: ColourResponseBody;
  name: string;
  trims: OfferedTrimRequestBody[];
  items: PricedItemChange[];
  readColour: (colourId: string) => Promise<ColourResponseBody>;
}): Omit<PricedItemBatchEntry, 'items'> => {
  const {colour, trims, items} = options;
  return {
    key: pricedItemGroupKeys.colour(colour.id),
    title: options.name,
    build: async () => {
      const current = await options.readColour(colour.id);
      if (!equals(storedRows(current.trims), storedRows(colour.trims))) {
        throw new PricedItemGroupEntryChanged();
      }
      return {
        items,
        operations: [
          {
            type: 'colourTrims.replace',
            targetId: colour.id,
            body: {trims, version: current.version},
          },
        ],
      };
    },
  };
};
